import { Box, Button, Chip, ChipProps, Container, Typography } from '@mui/material'
import { BackButton } from '../components/BackButton'
import { useContext, useState } from 'react'
import { HotelContext } from '../contexts/HotelContext'
import { DateTime } from 'luxon'
import { DateTimeFormats } from '../common/constants'
import { formatCurrency } from '../utils/numberUtil'
import { useQuery } from '@tanstack/react-query'
import { getCheckInReservation } from '../apis/checkinApis'
import { OverlayLoader } from '../components/Loader'
import { BookingStatus, PaymentStatus } from '../types/hotelTypes'
import { useNavigate } from 'react-router'
import { getNextPage } from '../utils/checkinUtils'
import { useSnackbar } from 'notistack'
import { Booking } from '../types/booking'

export const CheckInPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    hotel,
    reservation: { id: reservationId },
  } = useContext(HotelContext)
  const { data, isLoading, error } = useQuery({
    queryKey: ['getCheckInReservation', hotel.hotelId, reservationId],
    queryFn: () => getCheckInReservation(hotel.hotelId, reservationId),
  })
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const room = data?.ezcloud?.roomTypeId && hotel.rooms?.find((item) => item.roomTypeId === data?.ezcloud?.roomTypeId)

  if (isLoading || error) {
    return <OverlayLoader />
  }

  const isValidStatus = [BookingStatus.BOOKED, BookingStatus.CHECKIN].includes(data?.ezcloud?.bookingStatus)
  const isPaid = data?.paymentStatus === PaymentStatus.Paid
  const { label: statusLabel, color: statusColor } = getStatusLabel(data)

  function getStatusLabel(booking: Booking) {
    let label: string
    let color: ChipProps['color']
    switch (booking?.ezcloud?.bookingStatus) {
      case BookingStatus.BOOKED:
      case BookingStatus.CHECKIN:
        label = isPaid ? 'Đã thanh toán' : 'Chưa thanh toán'
        color = isPaid ? 'success' : 'warning'
        break
      case BookingStatus.CHECKOUT:
        label = 'Đã trả phòng'
        color = 'info'
        break
      case BookingStatus.CANCELLED:
        label = 'Đã hủy'
        color = 'error'
      default:
        break
    }

    return { label, color }
  }
  const onSubmit = async () => {
    setIsSubmitting(true)
    try {
      const { url } = getNextPage(data)
      navigate(url)
    } catch (err) {
      console.error(err)
      enqueueSnackbar('Đã có lỗi xảy ra. Vui lòng thử lại hoặc liên hệ lễ tân.', {
        variant: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Container maxWidth="sm">
      <BackButton to={`/hotel/${hotel.hotelId}`} />
      <Box py={1}>
        <Typography
          color={(theme) => theme.palette.primary.main}
          mt={1}
          textAlign="center"
          gutterBottom
          variant="h4"
          fontWeight="600">
          Thông tin đặt phòng
          <Typography color={(theme) => theme.palette.grey[600]} textAlign="center">
            Kiểm tra lại thông tin đặt phòng của bạn
          </Typography>
        </Typography>
      </Box>
      <Box>
        <Box>
          <Typography>
            <span>Mã đặt phòng: </span>
            <b>{data.ezcloud.reservationNumber}</b>
          </Typography>
        </Box>
        <Box>
          <Typography>
            <span>Nhận phòng: </span>
            <b>{DateTime.fromISO(data.from).toFormat(DateTimeFormats.FULL)}</b>
          </Typography>
        </Box>
        <Box>
          <Typography>
            <span>Trả phòng: </span>
            <b>{DateTime.fromISO(data.to).toFormat(DateTimeFormats.FULL)}</b>
          </Typography>
        </Box>
        <Box>
          <Typography>
            <span>Khách sạn: </span>
            <b>{hotel.hotelName}</b>
          </Typography>
        </Box>
        <Box>
          <Typography>
            <span>Loại phòng: </span>
            <b>{room.roomTypeName}</b>
          </Typography>
        </Box>
        {Boolean(data.totalPrice) && (
          <Box>
            <Typography>
              <span>Giá: </span>
              <b>{formatCurrency(data.totalPrice)}</b>
            </Typography>
          </Box>
        )}
        <Box>
          <Typography>
            <span>Số điện thoại: </span>
            <b>{data.phoneNumber}</b>
          </Typography>
        </Box>
        <Box>
          <Typography>
            <span>Tên liên hệ: </span>
            <b>{data.contactName}</b>
          </Typography>
        </Box>
        <Box>
          <Typography>
            <span>Tình trạng: </span>
            <Chip label={statusLabel} color={statusColor} />
          </Typography>
        </Box>
      </Box>
      <Box mt={4}>
        <Button
          disabled={isSubmitting || isLoading || !isValidStatus}
          fullWidth
          variant="contained"
          type="submit"
          onClick={onSubmit}>
          Tiếp tục
          {isSubmitting && <OverlayLoader />}
        </Button>
      </Box>
    </Container>
  )
}
